import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import api from "../../services/api";

const CalComIntegration = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://api.cal.com/v2");
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState("");
  const [calUsername, setCalUsername] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    fetchIntegrationStatus();
  }, []);

  useEffect(() => {
    if (integration && integration.configured) {
      fetchEventTypes();
      if (tabValue === 2) {
        fetchBookings();
      }
    }
  }, [integration, tabValue]);

  const fetchIntegrationStatus = async () => {
    try {
      const { data } = await api.get("/calcom/status");
      setIntegration(data);
    } catch (error) {
      // Integração não configurada
    }
  };

  const fetchBookings = async () => {
    setLoadingBookings(true);
    try {
      const statusResponse = await api.get("/calcom/status");
      if (!statusResponse.data.configured) {
        toast.error("Integração Cal.com não configurada");
        return;
      }

      const { data } = await api.get("/calcom/calcom-bookings");
      const bookingsArray = data.bookings || [];
      setBookings(bookingsArray);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Endpoint não encontrado - verifique se o backend foi atualizado");
      } else if (error.response?.status === 500) {
        toast.error("Erro interno do servidor: " + (error.response?.data?.message || error.message));
      } else {
        toast.error("Erro ao buscar reservas: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchEventTypes = async () => {
    setLoadingEventTypes(true);
    try {
      const { data } = await api.get("/calcom/event-types");
      
      const types = data.eventTypes || [];
      const username = data.username || '';
      
      setEventTypes(types);
      setCalUsername(username);
      
      if (types.length > 0 && !selectedEventType) {
        setSelectedEventType(types[0].slug);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Integração Cal.com não configurada");
      } else {
        toast.error("Erro ao buscar tipos de evento: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoadingEventTypes(false);
    }
  };

  const handleConfigureIntegration = async () => {
    if (!apiKey.trim()) {
      toast.error("API Key é obrigatória");
      return;
    }

    setLoading(true);
    try {
      await api.post("/calcom/configure", { apiKey, baseUrl });
      toast.success("Integração configurada com sucesso!");
      await fetchIntegrationStatus();
      setTabValue(0);
    } catch (error) {
      toast.error("Erro ao configurar: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiado!");
  };

  const getBookingUrl = () => {
    if (calUsername && selectedEventType) {
      return `https://cal.com/${calUsername}/${selectedEventType}`;
    }
    return null;
  };

  const getEmbedUrl = () => {
    const url = getBookingUrl();
    return url ? `${url}?embed=true&theme=light&hideEventTypeDetails=false` : null;
  };

  return (
    <div style={{ padding: "24px", background: "#f5f7fa", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
          📅 Cal Agenda
        </h1>
      </div>

      {/* Tabs */}
      <div
        style={{
          background: "white",
          borderRadius: "8px 8px 0 0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e8ecf1",
          borderBottom: "none",
          display: "flex",
          gap: "0",
        }}
      >
        {["Calendário", "Reservas"].map((tab, index) => (
          <button
            key={index}
            onClick={() => setTabValue(index)}
            style={{
              padding: "14px 24px",
              background: tabValue === index ? "white" : "transparent",
              color: tabValue === index ? "#3b82f6" : "#6b7280",
              border: "none",
              borderBottom: tabValue === index ? "2px solid #3b82f6" : "2px solid transparent",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: tabValue === index ? 600 : 500,
              transition: "all 0.2s ease",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        style={{
          background: "white",
          borderRadius: "0 0 8px 8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e8ecf1",
          padding: "24px",
          minHeight: "500px",
        }}
      >
        {/* Aba Calendário */}
        {tabValue === 0 && (
          <>
            {!integration?.configured ? (
              <div
                style={{
                  background: "#e3f2fd",
                  border: "1px solid #90caf9",
                  borderRadius: "8px",
                  padding: "16px",
                  color: "#1976d2",
                  fontSize: "14px",
                }}
              >
                ℹ️ Configure a integração na aba "Configuração" para visualizar o calendário.
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
                      Calendário de Agendamento
                    </h3>
                    <button
                      onClick={fetchEventTypes}
                      disabled={loadingEventTypes}
                      style={{
                        padding: "8px",
                        background: "none",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        cursor: loadingEventTypes ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        opacity: loadingEventTypes ? 0.5 : 1,
                      }}
                    >
                      <RefreshIcon style={{ fontSize: "18px", color: "#6b7280" }} />
                    </button>
                  </div>

                  {loadingEventTypes ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          border: "4px solid #e8ecf1",
                          borderTop: "4px solid #3b82f6",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          margin: "0 auto",
                        }}
                      />
                    </div>
                  ) : eventTypes.length === 0 ? (
                    <div
                      style={{
                        background: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "8px",
                        padding: "16px",
                        color: "#856404",
                        fontSize: "14px",
                      }}
                    >
                      ⚠️ Nenhum tipo de evento encontrado. Crie eventos no Cal.com primeiro.
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: "16px" }}>
                        <label style={{ fontSize: "13px", color: "#374151", fontWeight: 500, display: "block", marginBottom: "6px" }}>
                          Selecione o Tipo de Evento
                        </label>
                        <select
                          value={selectedEventType}
                          onChange={(e) => setSelectedEventType(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontFamily: "inherit",
                          }}
                        >
                          {eventTypes.map((eventType) => (
                            <option key={eventType.id} value={eventType.slug}>
                              {eventType.title} ({eventType.length} min)
                            </option>
                          ))}
                        </select>
                      </div>

                      {getBookingUrl() && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background: "#f9fafb",
                            padding: "12px",
                            borderRadius: "6px",
                            marginBottom: "16px",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              fontFamily: "monospace",
                              fontSize: "13px",
                              color: "#1a1a1a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getBookingUrl()}
                          </div>
                          <button
                            onClick={() => handleCopyLink(getBookingUrl())}
                            style={{
                              padding: "6px",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Copiar link"
                          >
                            <FileCopyIcon style={{ fontSize: "18px", color: "#6b7280" }} />
                          </button>
                          <button
                            onClick={() => window.open(getBookingUrl(), '_blank')}
                            style={{
                              padding: "6px",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Abrir em nova aba"
                          >
                            <OpenInNewIcon style={{ fontSize: "18px", color: "#6b7280" }} />
                          </button>
                        </div>
                      )}

                      {getEmbedUrl() ? (
                        <div
                          style={{
                            border: "1px solid #e8ecf1",
                            borderRadius: "8px",
                            overflow: "hidden",
                            minHeight: "650px",
                            background: "#fff",
                          }}
                        >
                          <iframe
                            key={selectedEventType}
                            src={getEmbedUrl()}
                            style={{
                              width: "100%",
                              height: "650px",
                              border: "none",
                            }}
                            title="Cal.com Booking Calendar"
                            allow="payment"
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            background: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: "8px",
                            padding: "16px",
                            color: "#856404",
                            fontSize: "14px",
                          }}
                        >
                          ⚠️ Não foi possível carregar o calendário. Verifique a configuração.
                        </div>
                      )}
                    </>
                  )}
                </div>

                {eventTypes.length > 0 && (
                  <div style={{ marginTop: "24px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", marginBottom: "8px" }}>
                      Links de Agendamento
                    </h3>
                    <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>
                      Compartilhe estes links com seus clientes.
                    </div>
                    
                    {eventTypes.map((eventType) => (
                      <div
                        key={eventType.id}
                        style={{
                          border: "1px solid #e8ecf1",
                          borderRadius: "6px",
                          padding: "12px",
                          marginBottom: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: 500, color: "#1a1a1a" }}>
                            {eventType.title}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            {eventType.length} minutos
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <button
                            onClick={() => handleCopyLink(eventType.bookingLink || `https://cal.com/${calUsername}/${eventType.slug}`)}
                            style={{
                              padding: "6px",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Copiar link"
                          >
                            <FileCopyIcon style={{ fontSize: "18px", color: "#6b7280" }} />
                          </button>
                          <button
                            onClick={() => window.open(eventType.bookingLink || `https://cal.com/${calUsername}/${eventType.slug}`, '_blank')}
                            style={{
                              padding: "6px",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Abrir"
                          >
                            <OpenInNewIcon style={{ fontSize: "18px", color: "#6b7280" }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Aba Reservas */}
        {tabValue === 1 && (
          <>
            {!integration?.configured ? (
              <div
                style={{
                  background: "#e3f2fd",
                  border: "1px solid #90caf9",
                  borderRadius: "8px",
                  padding: "16px",
                  color: "#1976d2",
                  fontSize: "14px",
                }}
              >
                ℹ️ Configure a integração na aba "Configuração" para visualizar as reservas.
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
                    Reservas do Cal.com
                  </h3>
                  <button
                    onClick={fetchBookings}
                    disabled={loadingBookings}
                    style={{
                      padding: "8px",
                      background: "none",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      cursor: loadingBookings ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      opacity: loadingBookings ? 0.5 : 1,
                    }}
                  >
                    <RefreshIcon style={{ fontSize: "18px", color: "#6b7280" }} />
                  </button>
                </div>

                {loadingBookings ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        border: "4px solid #e8ecf1",
                        borderTop: "4px solid #3b82f6",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto",
                      }}
                    />
                  </div>
                ) : bookings.length === 0 ? (
                  <div
                    style={{
                      background: "#e3f2fd",
                      border: "1px solid #90caf9",
                      borderRadius: "8px",
                      padding: "16px",
                      color: "#1976d2",
                      fontSize: "14px",
                    }}
                  >
                    ℹ️ Nenhuma reserva encontrada no Cal.com.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {bookings.map((booking) => (
                      <div
                        key={booking.id}
                        style={{
                          border: "1px solid #e8ecf1",
                          borderRadius: "8px",
                          padding: "20px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", marginBottom: "12px" }}>
                              {booking.title || 'Agendamento'}
                            </h4>
                            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "6px" }}>
                              <strong>Data:</strong> {new Date(booking.startTime).toLocaleDateString('pt-BR')}
                            </div>
                            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "6px" }}>
                              <strong>Horário:</strong> {new Date(booking.startTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} - {new Date(booking.endTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                            {booking.attendees && booking.attendees.length > 0 && (
                              <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "12px" }}>
                                <strong>Participante:</strong> {booking.attendees[0].name} ({booking.attendees[0].email})
                              </div>
                            )}
                            {booking.meetingUrl && (
                              <button
                                onClick={() => window.open(booking.meetingUrl, '_blank')}
                                style={{
                                  padding: "8px 16px",
                                  background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  transition: "all 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "translateY(-1px)";
                                  e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "none";
                                  e.target.style.boxShadow = "none";
                                }}
                              >
                                <OpenInNewIcon style={{ fontSize: "16px" }} />
                                Entrar na Reunião
                              </button>
                            )}
                          </div>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: 600,
                              background: booking.status === 'ACCEPTED' ? "#e0e7ff" : 
                                         booking.status === 'PENDING' ? "#f3f4f6" :
                                         booking.status === 'CANCELLED' ? "#fee2e2" : "#f3f4f6",
                              color: booking.status === 'ACCEPTED' ? "#4f46e5" : 
                                     booking.status === 'PENDING' ? "#6b7280" :
                                     booking.status === 'CANCELLED' ? "#dc2626" : "#6b7280",
                            }}
                          >
                            {booking.status === 'ACCEPTED' ? 'Confirmado' : 
                             booking.status === 'PENDING' ? 'Pendente' :
                             booking.status === 'CANCELLED' ? 'Cancelado' : booking.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default CalComIntegration;
